import { useCallback, useEffect, useRef } from 'react';

import { useEditorContext } from 'utils/useEditorContext';
import { useServerStorage } from 'utils/useServerStorage';
import { useCurrentUser } from 'utils/user/useCurrentUser';

import { useRunAiAgent } from './useRunAiAgent';

interface UseQuickAiDraftOptions {
  dixaConversationId: string;
  /** Called once the draft has been inserted into the editor. */
  onInserted?: () => void;
  /** Called when the draft could not be generated. */
  onError?: () => void;
  /** How the draft enters the editor. Defaults to `replace`. */
  insert?: 'replace' | 'insertBelow';
}

export interface UseQuickAiDraftResult {
  /** Generates a draft and inserts it into the editor. */
  generateDraft: () => void;
  /** Whether a default AI agent is set to draft with. */
  hasAgent: boolean;
  isLoading: boolean;
}

/**
 * A hook for generating a reply draft with the default AI agent and inserting
 * it into the editor, without any intermediate preview.
 */
export function useQuickAiDraft({
  dixaConversationId,
  onInserted,
  onError,
  insert = 'replace',
}: UseQuickAiDraftOptions): UseQuickAiDraftResult {
  const { user } = useCurrentUser();
  const { insertBelow, replace } = useEditorContext();
  const insertDraft = insert === 'replace' ? replace : insertBelow;
  const { data: userStorageData, isLoading: isLoadingPreference } =
    useServerStorage('user');
  const defaultAgentId = userStorageData.agent_copilot?.defaultDraftAgentId;

  const { runAgent, text, isLoading, isError, isSuccess } = useRunAiAgent(
    defaultAgentId ?? '',
    dixaConversationId,
    user.id,
  );

  // Latches that a started run still needs its text inserted.
  const awaitsInsertRef = useRef(false);

  const generateDraft = useCallback(() => {
    if (!defaultAgentId || !dixaConversationId) {
      return;
    }

    awaitsInsertRef.current = true;
    runAgent();
  }, [defaultAgentId, dixaConversationId, runAgent]);

  useEffect(() => {
    if (!awaitsInsertRef.current || isLoading) {
      return;
    }

    if (isSuccess) {
      awaitsInsertRef.current = false;

      if (text) {
        insertDraft(text);
        onInserted?.();
      } else {
        onError?.();
      }

      return;
    }

    if (isError) {
      awaitsInsertRef.current = false;
      onError?.();
    }
  }, [isLoading, isSuccess, isError, text, insertDraft, onInserted, onError]);

  return {
    generateDraft,
    hasAgent: !!defaultAgentId,
    isLoading: isLoadingPreference || isLoading,
  };
}
